import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion, requestIdleCallback } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';

/** Request locale-aware product fields; Shopify controls actual result ranking.
 * @param {string} searchTerm
 * @param {string} route
 * @param {string} origin
 * @returns {URL}
 */
function buildPredictiveSearchURL(searchTerm, route, origin) {
  const url = new URL(route, origin);
  url.searchParams.set('q', searchTerm);
  // Return real destinations, not legacy supplier-worded query suggestions.
  url.searchParams.set('resources[type]', 'product,collection,page,article');
  url.searchParams.set('resources[limit_scope]', 'each');
  url.searchParams.set('resources[limit]', '6');
  url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,variants.sku');
  url.searchParams.set('resources[options][unavailable_products]', 'last');
  return url;
}

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement} [searchStatus] - Loading/error feedback outside the replaceable results.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;
  #resultsQuery = '';

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    if (this.#controller.signal.aborted) this.#controller = new AbortController();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    // A closed search dialog must not compete with the product's initial load.
    if (!dialog && RecentlyViewed.getProducts().length > 0) {
      requestIdleCallback(() => {
        if (this.isConnected && !this.refs.searchInput.value.trim()) this.#loadEmptyState();
      });
    }
    if (dialog?.querySelector('dialog')?.open) this.#handleDialogOpen();
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    this.#activeFetch?.abort();
    this.search.cancel();
    this.resetSearch.cancel();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      event.preventDefault();
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.search.cancel();
    this.resetSearch.cancel();
    this.#activeFetch?.abort();
    this.#clearQuery();
    this.#setRequestStatus('');
    this.refs.searchInput.setAttribute('aria-expanded', 'false');
  };

  #handleDialogOpen = () => {
    if (!this.refs.searchInput.value.trim()) {
      if (this.#emptyStateLoaded) this.#setRequestStatus('');
      else this.#loadEmptyState();
    }
  };

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    this.#resetSearch();
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.defaultPrevented) return;
    if (event.key === 'Escape') {
      // The dialog's Escape handler closes it; do not start a hidden reset fetch.
      if (!this.dialog) this.#resetSearch();
      return;
    }
    // Links and buttons keep native Enter/Tab behavior; only the input owns arrows.
    if (event.target !== this.refs.searchInput) return;

    if (event.key === 'Enter' && this.refs.searchInput.value.trim() !== this.#resultsQuery) {
      event.preventDefault();
      this.#submitSearch();
      return;
    }
    if (this.refs.searchInput.value.trim() !== this.#resultsQuery) return;

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          event.preventDefault();
          this.#submitSearch();
        }
        break;
      }
    }
  };

  #submitSearch() {
    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', this.refs.searchInput.value);
    url.searchParams.set('type', 'product,page,article');
    url.searchParams.set('options[prefix]', 'last');
    window.location.href = url.toString();
  }

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    // Autofill and assistive input can dispatch input without inputType.
    // Ignore only an unfinished IME composition, not valid field changes.
    if (event.isComposing) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = buildPredictiveSearchURL(searchTerm, Theme.routes.predictive_search_url, location.origin);

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();
    this.#emptyStateLoaded = false;
    this.#setRequestStatus('loading');
    try {
      const resultsMarkup = await sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
      if (!this.#isCurrentRequest(abortController, searchTerm)) return;
      const section = new DOMParser().parseFromString(resultsMarkup, 'text/html')
        .getElementById(`shopify-section-${this.dataset.sectionId}`);
      if (!section?.querySelector('#predictive-search-results')) throw new Error('Search response did not contain results');
      morph(predictiveSearchResults, section);
      this.#resultsQuery = searchTerm;
      this.#setRequestStatus('');
      this.#resetScrollPositions();
    } catch {
      if (this.#isCurrentRequest(abortController, searchTerm)) this.#setRequestStatus('error');
    }
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #isCurrentRequest(controller, query) {
    return !controller.signal.aborted && this.isConnected &&
      (!this.dialog || this.dialog.querySelector('dialog')?.open) &&
      this.refs.searchInput.value.trim() === query;
  }

  #setRequestStatus(state) {
    const { searchStatus, predictiveSearchResults, searchInput } = this.refs;
    predictiveSearchResults.hidden = Boolean(state);
    predictiveSearchResults.setAttribute('aria-busy', String(state === 'loading'));
    searchInput.setAttribute('aria-expanded', String(!state && this.#allResultsItems.length > 0));
    if (searchStatus) {
      searchStatus.textContent = state ? searchStatus.dataset[state] : '';
      searchStatus.hidden = !state;
    }
  }

  #clearQuery() {
    this.#currentIndex = -1;
    this.refs.searchInput.value = '';
    this.#hideResetButton();
  }

  #resetSearch = async () => {
    const { predictiveSearchResults } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#clearQuery();
    if (this.#emptyStateLoaded) return;
    if (this.dialog && !this.dialog.querySelector('dialog')?.open) return;

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    this.#setRequestStatus('loading');
    try {
      const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, true, url);
      const parsedEmptySectionMarkup = new DOMParser()
        .parseFromString(emptySectionMarkup, 'text/html')
        .querySelector('.predictive-search-empty-section');

      if (!parsedEmptySectionMarkup?.querySelector('#predictive-search-results')) throw new Error('No empty section markup found');

      const viewedProducts = RecentlyViewed.getProducts();
      if (viewedProducts.length > 0 && this.#isCurrentRequest(abortController, '')) {
        // Recently viewed is an enhancement; a failed lookup must not hide the catalog.
        const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup().catch(() => null);
        if (recentlyViewedMarkup) {
          const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
          const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
          for (const child of recentlyViewedProductsHtml?.children ?? []) {
            if (child instanceof HTMLElement) child.setAttribute('ref', 'recentlyViewedWrapper');
          }
          const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
          if (collectionElement && recentlyViewedProductsHtml) collectionElement.prepend(...recentlyViewedProductsHtml.children);
        }
      }
      if (!this.#isCurrentRequest(abortController, '')) return;
      morph(predictiveSearchResults, parsedEmptySectionMarkup);
      this.#emptyStateLoaded = true;
      this.#resultsQuery = '';
      this.#setRequestStatus('');
      this.#resetScrollPositions();
    } catch {
      if (this.#isCurrentRequest(abortController, '')) this.#setRequestStatus('error');
    }
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
